import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../experience/api";
import { DISH_PHOTOS, normName } from "../table/dishPhotos";
import "./landing.css";

const HERO_PICKS = ["butter chicken", "garlic naan", "chicken tikka masala", "dal makhani", "biryani", "samosa", "tandoori", "gulab jamun", "chole", "paneer"];

export const CUTOUTS = [
  { match: "butter chicken", src: "/assets/dish-butter-chicken.png" },
  { match: "biryani", src: "/assets/dish-biryani.png" },
  { match: "samosa", src: "/assets/dish-samosa.png" },
  { match: "naan", src: "/assets/dish-naan.png" },
];
const NO_PHOTO = [];

export const dishArt = (d) => {
  const n = (d.name || "").toLowerCase();
  if (NO_PHOTO.some((b) => n.includes(b))) return { kind: "none" };
  const c = CUTOUTS.find((x) => n.includes(x.match));
  if (c) return { kind: "cut", src: c.src };
  if (d.img) return { kind: "photo", src: d.img };
  return { kind: "none" };
};

export const DishArt = ({ d, size = 104 }) => {
  const a = dishArt(d);
  if (a.kind === "cut") return <img className="v2-cutimg" src={a.src} alt={d.name} loading="lazy" style={{ maxHeight: size }} />;
  if (a.kind === "photo") return <span className="v2-ph" style={{ width: size, height: size }}><img src={a.src} alt={d.name} loading="lazy" /></span>;
  return <span className="v2-noph" style={{ width: size, height: size }}><span>{d.name}</span></span>;
};

export const addToSharedCart = (d) => {
  let cart = {};
  try { cart = JSON.parse(localStorage.getItem("up_cart") || "{}"); } catch { cart = {}; }
  cart[d.slug] = { slug: d.slug, name: d.name, price: d.price, image: d.img || null, qty: (cart[d.slug]?.qty || 0) + 1 };
  localStorage.setItem("up_cart", JSON.stringify(cart));
};

const SCAN_GOLD = "#D4AF37";
const parseTableCode = (text) => {
  try {
    let path;
    const t = String(text || "").trim();
    if (/^https?:\/\//i.test(t)) {
      const u = new URL(t);
      const host = u.hostname.toLowerCase().replace(/^www\./, "");
      if (host !== "myurbanpassport.com" && host !== window.location.hostname.toLowerCase()) return null;
      path = u.pathname;
    } else if (t.startsWith("/")) path = t;
    else return null;
    const m = path.match(/^\/t\/([A-Za-z0-9-]+)\/?$/);
    return m ? m[1] : null;
  } catch { return null; }
};

const ScanModal = ({ onClose }) => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const cleanupRef = useRef(() => {});
  const doneRef = useRef(false);
  const [mode, setMode] = useState("starting"); // starting | native | lib | manual
  const [msg, setMsg] = useState("");
  const [tableNum, setTableNum] = useState("");

  const finish = (table) => {
    if (doneRef.current) return;
    doneRef.current = true;
    cleanupRef.current();
    onClose();
    navigate(`/t/${table}`);
  };

  const handleText = (text) => {
    const table = parseTableCode(text);
    if (table) { finish(table); return; }
    setMsg("That is not an Urban Turban table code");
    setTimeout(() => setMsg(""), 2600);
  };

  useEffect(() => {
    let stream = null;
    let interval = null;
    let qr = null;
    let cancelled = false;
    const stopAll = () => {
      if (interval) clearInterval(interval);
      if (stream) stream.getTracks().forEach((tr) => tr.stop());
      if (qr) { try { qr.stop().catch(() => {}); } catch { /* idle */ } }
    };
    cleanupRef.current = stopAll;
    (async () => {
      try {
        if ("BarcodeDetector" in window) {
          stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
          if (cancelled) { stopAll(); return; }
          setMode("native");
          await new Promise((r) => setTimeout(r, 60));
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            await videoRef.current.play().catch(() => {});
          }
          const det = new window.BarcodeDetector({ formats: ["qr_code"] });
          interval = setInterval(async () => {
            try {
              if (!videoRef.current) return;
              const codes = await det.detect(videoRef.current);
              if (codes.length) handleText(codes[0].rawValue);
            } catch { /* frame not ready */ }
          }, 350);
        } else {
          const { Html5Qrcode } = await import("html5-qrcode");
          if (cancelled) return;
          setMode("lib");
          await new Promise((r) => setTimeout(r, 60));
          qr = new Html5Qrcode("up-qr-reader");
          await qr.start({ facingMode: "environment" }, { fps: 10, qrbox: 250 }, (txt) => handleText(txt), () => {});
        }
      } catch {
        stopAll();
        if (!cancelled) setMode("manual");
      }
    })();
    return () => { cancelled = true; stopAll(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const goManual = () => {
    const n = tableNum.trim();
    if (n) finish(n);
  };

  return (
    <div data-testid="scan-modal" style={{ position: "fixed", inset: 0, zIndex: 100, background: "#0c0705", display: "flex", flexDirection: "column", color: "#f3e7d0", fontFamily: "Poppins, sans-serif" }}>
      <div style={{ display: "flex", alignItems: "center", padding: "14px 16px" }}>
        <h2 className="v2-serif" style={{ margin: 0, fontSize: 20, color: "#F4A300" }}>Scan your table QR</h2>
        <button data-testid="scan-close" onClick={() => { cleanupRef.current(); onClose(); }} aria-label="Close scanner"
          style={{ marginLeft: "auto", width: 38, height: 38, borderRadius: "50%", border: `1.5px solid ${SCAN_GOLD}`, background: "transparent", color: SCAN_GOLD, fontSize: 18, fontWeight: 800, cursor: "pointer" }}>✕</button>
      </div>
      <div style={{ flex: 1, position: "relative", margin: "0 14px 14px", border: `2px solid ${SCAN_GOLD}`, borderRadius: 20, overflow: "hidden", background: "#17100a", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {mode === "starting" && <p style={{ color: "#9b8a68", fontSize: 14 }}>Opening camera…</p>}
        {mode === "native" && <video ref={videoRef} playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
        {mode === "lib" && <div id="up-qr-reader" style={{ width: "100%" }} />}
        {mode === "manual" && (
          <div data-testid="scan-manual" style={{ padding: 22, textAlign: "center", maxWidth: 340 }}>
            <p style={{ fontSize: 14.5, lineHeight: 1.5, color: "#e8d9bd" }}>Camera blocked. Scan the QR on your table with your phone camera, or enter your table number</p>
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <input data-testid="scan-table-input" value={tableNum} onChange={(e) => setTableNum(e.target.value)} placeholder="Table number"
                inputMode="numeric" onKeyDown={(e) => e.key === "Enter" && goManual()}
                style={{ flex: 1, minWidth: 0, background: "#1c110a", border: `1.5px solid ${SCAN_GOLD}`, borderRadius: 999, color: "#f3e7d0", padding: "12px 16px", fontSize: 15, fontFamily: "Poppins, sans-serif" }} />
              <button data-testid="scan-table-go" onClick={goManual}
                style={{ border: "none", borderRadius: 999, background: "linear-gradient(#F4A300, #cf8a00)", color: "#1b0f06", fontWeight: 800, fontSize: 15, padding: "12px 22px", cursor: "pointer" }}>Go</button>
            </div>
          </div>
        )}
        {(mode === "native" || mode === "lib") && (
          <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "14px 16px", textAlign: "center", background: "linear-gradient(transparent, rgba(12,7,5,0.9))" }}>
            <p data-testid="scan-msg" style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: msg ? "#e05a3a" : "#d9c69a" }}>
              {msg || "Point your camera at the QR code on your table"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const Embers = () => (
  <div className="v2-embers" aria-hidden="true">
    {[...Array(14)].map((_, i) => (
      <span key={i} style={{ left: `${(i * 7.3 + 4) % 96}%`, top: `${(i * 13.7 + 6) % 92}%`, animationDelay: `${(i % 7) * 0.9}s`, width: i % 3 ? 4 : 6, height: i % 3 ? 4 : 6 }} />
    ))}
  </div>
);

const BabajiCorner = ({ pose }) => {
  if (!pose) return null;
  const src = pose === "thumbs" ? "/assets/babaji-thumbs.png" : "/assets/babaji-elder.png";
  const say = pose === "thumbs" ? "Shabash! Good pick" : pose === "wave" ? "Ji aayan nu!" : "Khel ke dekho!";
  return (
    <div className="v2-corner" data-testid="v2-babaji-corner">
      <span className="say">{say}</span><br />
      <img src={src} alt="" />
    </div>
  );
};

export default function LandingV2() {
  const navigate = useNavigate();
  const today = new Date().toDateString();
  const [stage, setStage] = useState(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return "open";
    return localStorage.getItem("v2_intro_day") === today ? "short" : "full";
  });
  const [dishes, setDishes] = useState([]);
  const [catOrder, setCatOrder] = useState([]);
  const [pose, setPose] = useState("wave");
  const [cartCount, setCartCount] = useState(() => {
    try { return Object.values(JSON.parse(localStorage.getItem("up_cart") || "{}")).reduce((n, i) => n + i.qty, 0); } catch { return 0; }
  });
  const playRef = useRef(null);
  const poseTimer = useRef(null);
  const [scanOpen, setScanOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("v2_intro_day", today);
    if (stage !== "open") {
      const t = setTimeout(() => setStage("open"), stage === "full" ? 3600 : 1100);
      return () => clearTimeout(t);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/table-menu").then((r) => {
      setCatOrder(r.data.categories || []);
      setDishes((r.data.dishes || []).map((d) => ({ ...d, img: d.image || DISH_PHOTOS[normName(d.name)] || null })));
    }).catch(() => setDishes([]));
    poseTimer.current = setTimeout(() => setPose(null), 3500);
    return () => clearTimeout(poseTimer.current);
  }, []);

  useEffect(() => {
    if (!playRef.current) return;
    const io = new IntersectionObserver((es) => {
      if (es[0].isIntersecting) { showPose("point"); io.disconnect(); }
    }, { threshold: 0.5 });
    io.observe(playRef.current);
    return () => io.disconnect();
  }, [dishes]); // eslint-disable-line react-hooks/exhaustive-deps

  const showPose = (p) => {
    clearTimeout(poseTimer.current);
    setPose(p);
    poseTimer.current = setTimeout(() => setPose(null), 2400);
  };

  const heroDishes = useMemo(() => {
    const out = [];
    const used = new Set();
    for (const key of HERO_PICKS) {
      const d = dishes.find((x) => x.img && !used.has(x.slug) && x.name.toLowerCase().includes(key));
      if (d) { out.push(d); used.add(d.slug); }
      if (out.length >= 8) break;
    }
    for (const d of dishes) {
      if (out.length >= 8) break;
      if (d.img && !used.has(d.slug)) { out.push(d); used.add(d.slug); }
    }
    return out;
  }, [dishes]);

  const heroCats = useMemo(() => {
    const out = [];
    for (const c of catOrder) {
      const d = dishes.find((x) => x.category === c && x.img);
      if (d) out.push({ name: c, img: d.img });
      if (out.length >= 4) break;
    }
    return out;
  }, [catOrder, dishes]);

  const addDish = (d) => {
    addToSharedCart(d);
    setCartCount((n) => n + 1);
    showPose("thumbs");
    toast.success(`${d.name} added`, { description: "Your cart is ready whenever you are", style: { background: "#221408", color: "#F4A300", border: "1px solid #D4AF37" } });
  };

  return (
    <div className="v2-root">
      <Embers />
      <div className="v2-wrap">
        <header className="v2-head">
          <img src="/urban-logo.png" alt="Urban Turban" loading="eager" />
          <div>
            <h1 className="v2-serif">Urban Turban</h1>
            <p className="v2-tag v2-serif">Chicago's Modern Indian Dhaba</p>
            <p className="v2-cuisine">INDIAN · PUNJABI · PAKISTANI</p>
          </div>
          <button className="v2-scan" data-testid="v2-scan-table" onClick={() => setScanOpen(true)}>Scan table</button>
        </header>
        {scanOpen && <ScanModal onClose={() => setScanOpen(false)} />}

        <span className="v2-rasoi">BABAJI DI RASOI</span>

        <section className="v2-stage" data-testid="v2-hero-stage">
          <div className="v2-glow" />
          {stage === "open" && (
            <>
              <div className="v2-bubble">
                <h2 className="v2-serif" data-testid="v2-greeting">Sat Sri Akal Ji! Ki khaoge?</h2>
                <p>Indian Punjabi food, made fresh. Order from anywhere.</p>
              </div>
              {heroCats.map((c, i) => (
                <button key={c.name} className={`v2-cat c${i}`} data-testid={`v2-cat-${i}`} onClick={() => navigate(`/m?c=${encodeURIComponent(c.name)}`)}>
                  <span className="v2-ph"><img src={c.img} alt={c.name} loading="lazy" /></span>
                  <span className="lbl">{c.name}</span>
                </button>
              ))}
              <img className="v2-babaji" src="/assets/babaji-elder.png" alt="Babaji greets you" data-testid="v2-babaji-hero" />
            </>
          )}
          {stage !== "open" && (
            <>
              <div className="v2-doors opening" style={{ "--dur": stage === "full" ? "1.6s" : "0.7s", "--delay": stage === "full" ? "0.6s" : "0.1s" }} data-testid="v2-doors">
                <div className="v2-door l"><span className="knob" /></div>
                <div className="v2-door r"><span className="knob" /></div>
              </div>
              <div className="v2-doorlight" style={{ "--delay": stage === "full" ? "0.6s" : "0.1s" }} />
              <div className="v2-smoke" style={{ left: "22%", "--delay": stage === "full" ? "0.8s" : "0.15s" }} />
              <div className="v2-smoke" style={{ right: "18%", "--delay": stage === "full" ? "1.1s" : "0.2s" }} />
              <button className="v2-skip" data-testid="v2-skip-intro" onClick={() => setStage("open")}>Skip</button>
            </>
          )}
        </section>

        <div className="v2-cta-row">
          <a className="v2-cta fill" data-testid="v2-pickup" href="https://tryurbanturban.com" target="_blank" rel="noreferrer">Pickup</a>
          <a className="v2-cta fill" data-testid="v2-delivery" href="https://tryurbanturban.com" target="_blank" rel="noreferrer">Delivery</a>
          <button className="v2-cta line" data-testid="v2-full-menu" onClick={() => navigate("/m")}>Full Menu</button>
        </div>

        <p className="v2-sec">STRAIGHT FROM THE TANDOOR</p>
        <div className="v2-dishrow" data-testid="v2-dish-row">
          {heroDishes.map((d) => (
            <div className="v2-dish" key={d.slug} data-testid={`v2-dish-${d.slug}`}>
              <div className="art"><DishArt d={d} /></div>
              <p className="nm">{d.name}</p>
              <p className="pr">${Number(d.price).toFixed(2)}</p>
              <button className="v2-add" data-testid={`v2-add-${d.slug}`} onClick={() => addDish(d)}>+ Add</button>
            </div>
          ))}
          {heroDishes.length === 0 && <p style={{ color: "#9b8a68", fontSize: 13 }}>Loading the kitchen…</p>}
        </div>

        <p className="v2-sec" ref={playRef}>PLAY WHILE YOU WAIT · JUST FOR FUN</p>
        <div className="v2-playrow">
          <Link className="v2-game" data-testid="v2-play-naan" to="/naan">
            <h3>Catch the Naan</h3><p>Tandoor frenzy</p>
            <img src="/assets/naan.png" alt="" loading="lazy" />
          </Link>
          <Link className="v2-game" data-testid="v2-play-mela" to="/experience/arcade">
            <h3>Mela Da Chakkar</h3><p>Spin the wheel</p>
            <img className="ph" src="/dish-photos/gulab-jamun.jpg" alt="" loading="lazy" />
          </Link>
          <Link className="v2-game" data-testid="v2-play-truck" to="/experience/arcade">
            <h3>Truck to Mela</h3><p>Drive and dodge</p>
            <img src="/assets/babaji-elder.png" alt="" loading="lazy" />
          </Link>
        </div>
        <Link className="v2-game" style={{ marginTop: 10 }} data-testid="v2-ask-veergi" to="/experience/veergi">
          <h3>Ask VeerGi Q&A</h3><p>Your Punjabi food guide, any language</p>
        </Link>

        <Link className="v2-gold" data-testid="v2-gold-banner" to="/gold">
          <img src="/gold/coin.png" alt="Gold coin" loading="lazy" />
          <div>
            <b>Dine In · Monthly draw of a 1 gram gold coin</b>
            <span>Scan the QR on your paid receipt. Dine-in only · tap for rules</span>
          </div>
        </Link>

        <div className="v2-table" data-testid="v2-table-card">
          <b>At your table?</b><br />
          Scan the table QR for Pay Bill and Dine-in Rewards
        </div>

        <p className="v2-foot">2660 N Halsted · Indian Punjabi kitchen · Chicago</p>
      </div>

      <BabajiCorner pose={pose} />
      {cartCount > 0 && (
        <button className="v2-cartchip" data-testid="v2-cart-chip" onClick={() => navigate("/go/cart")}>
          View cart · {cartCount} item{cartCount > 1 ? "s" : ""}
        </button>
      )}
    </div>
  );
}
